import {
  HomeIcon,
  LinkIcon,
  SparklesIcon,
  CloudIcon,
  InboxIcon,
  CreditCardIcon,
  CpuChipIcon,
  ChatBubbleLeftIcon,
  ChartBarSquareIcon,
  BoltIcon,
  BellAlertIcon,
  CubeIcon,
  PhoneIcon,
  RectangleStackIcon,
  UsersIcon,
  BuildingOfficeIcon,
  RocketLaunchIcon,
  ScaleIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

import { canReviewMarketplaceExecutions } from '../../services/marketplaceReview';
import { canPublishMarketplaceOffers } from '../../services/marketplacePublish';

/*
 * What the platform sidebar contains, for a given viewer, as data.
 *
 * This used to live inside Layout, which is fine while Layout is the only thing
 * that draws a sidebar. It stopped being the only thing: the marketing homepage
 * mounts the real Dashboard behind its Control surface, and a dashboard without
 * the platform nav beside it is not the product — it is a panel. Copying the
 * array into the marketing tree would mean a nav item added to the app silently
 * failing to appear in the picture the homepage uses to sell it, which is the
 * exact class of drift that makes screenshots lie.
 *
 * So the model is here, both consumers read it, and the only thing Layout kept
 * is what Layout alone has: routing, auth, and its own chrome.
 *
 * Pure: it takes the viewer and the hostname and returns items. Nothing here
 * reads window, so it renders identically on the server and in a test.
 */

/**
 * The audience flags every visibility decision below is written against.
 * Exported because the same questions are asked outside the nav, and because a
 * test that pins the flags is far more legible than one that pins their effects.
 */
export function resolvePlatformAudience(user, hostname = '') {
  const tenantType = user?.tenant?.tenant_type || user?.tenant_type || user?.user_type;
  const isBluebearEmployee = user?.isBluebearEmployee ?? user?.is_bluebear_employee;
  const host = String(hostname || '');
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const isExecgptHost = host.endsWith('execgpt.com') || host.includes('execgpt');
  const isBluebearHost = host.endsWith('bluebear.ai');

  const platformRoles = new Set(['admin', 'platform', 'tenant']);
  /* `tenant_type === 'platform'` means "sits in the NIL platform tenant", which
   * is the DEFAULT bucket for any account with no tenant membership — it is not
   * a grant of operator authority. A plain role=user in NIL is an ordinary end
   * user, so membership alone must never promote them; it counts only alongside
   * an operator role or the employee flag. (Same conflation the gateway fixed in
   * principal.ts `scopeFor`: NIL is a fallback, not a privilege.) */
  const isPlatformUser = platformRoles.has(user?.role) || isBluebearEmployee;

  const isWhiteLabelUser = ['white-label', 'reseller'].includes(tenantType) || isPlatformUser;
  const isDirectCustomer = tenantType === 'end-customer';

  return {
    tenantType,
    isPlatformUser,
    isDirectCustomer,
    isRegularUser: user?.role === 'user',
    isAdmin: user?.role === 'admin',
    /* The marketplace attestation queue. Permission-gated, never role-gated:
     * the gateway keeps `review_marketplace_executions` out of ROLE_PERMISSIONS
     * on purpose, so the nav entry appears only for a viewer who actually
     * carries the grant. */
    canReviewMarketplace: canReviewMarketplaceExecutions(user),
    /* The seller workspace. Same rule: `publish_marketplace_offers` is not in
     * ROLE_PERMISSIONS because publishing is invite-only during the pilot, so
     * the entry appears only for a viewer who actually carries the grant. */
    canPublishMarketplace: canPublishMarketplaceOffers(user),
    /* Tenant catalog and enablement remain admin-only; personal connections
     * on Integrations use the same user-scoped controls as chat. */
    canManageTenantCapabilities: user?.role === 'admin' || user?.role === 'tenant',
    canManageVoiceInfra:
      user?.role === 'admin' || user?.role === 'bluebear_admin' || user?.role === 'tenant',
    showCloudManager: isLocalHost || (isBluebearHost && !isExecgptHost),
    /* WHICH white-label surface a viewer gets is an entitlement question, not a
     * host question: only a viewer who actually has white-label/reseller access
     * sees the management dashboard; everyone else is offered the request form.
     * `isLocalHost` only decides whether the white-label surfaces show up at
     * all (host visibility) — it must NOT promote a regular user to the admin
     * dashboard, which is what made localhost show the wrong view entirely. */
    showWhiteLabelTab: isWhiteLabelUser,
    showWhiteLabelRequest: !isWhiteLabelUser && (isBluebearHost || isLocalHost),
  };
}

/**
 * The sidebar for one viewer: the main item list (already filtered) and the
 * collapsible Admin group that sits directly under Dashboard.
 *
 * `section` on an item opens a labelled group at that point in the list — it is
 * a heading, not a container, so the list stays flat and a new group is one
 * property rather than a new nesting level.
 */
export function buildPlatformNav({ user, hostname = '' } = {}) {
  const audience = resolvePlatformAudience(user, hostname);

  const items = [{ name: 'Dashboard', href: '/dashboard', icon: HomeIcon }];

  if (audience.isDirectCustomer) {
    items.push({ name: 'Compute', href: '/cloud-manager', icon: CloudIcon });
  } else if (audience.isPlatformUser) {
    items.push({ name: 'Cloud Manager', href: '/cloud-manager', icon: CloudIcon });
  }

  items.push(
    { name: 'Credits', href: '/credits', icon: CreditCardIcon },
    { name: 'Integrations', href: '/integrations', icon: LinkIcon },
    // Public discovery is available without a seller or reviewer grant.
    { name: 'Browse marketplace', href: '/marketplace', icon: RectangleStackIcon },
    { name: 'Models', href: '/models', icon: CpuChipIcon, section: 'AI Platform' },
    { name: 'Workspaces', href: '/workspaces', icon: CubeIcon },
    { name: 'Agents', href: '/ai-agents', icon: BoltIcon },
    { name: 'Workflows', href: '/workflows', icon: RectangleStackIcon },
    { name: 'Chat', href: '/ai-chat', icon: ChatBubbleLeftIcon },
    { name: 'Sessions', href: '/ai-sessions', icon: ChartBarSquareIcon },
    { name: 'Invocations', href: '/invocations', icon: BoltIcon },
    { name: 'Policies', href: '/routing-rules', icon: ScaleIcon },
  );

  if (audience.canReviewMarketplace) {
    items.push({ name: 'Report Review', href: '/marketplace/review', icon: ClipboardDocumentCheckIcon });
  }

  if (audience.canPublishMarketplace) {
    items.push({ name: 'Marketplace', href: '/marketplace/sell', icon: BanknotesIcon });
  }

  if (audience.canManageVoiceInfra) {
    items.push({ name: 'Voice Infrastructure', href: '/voice-infrastructure', icon: PhoneIcon });
  }

  // BlueBear's admins manage tenants under Admin → Tenants; everyone else follows their own request here.
  if (audience.showWhiteLabelTab && !audience.isAdmin) {
    items.push({ name: 'White-Label', href: '/white-label', icon: SparklesIcon });
  } else if (audience.showWhiteLabelRequest) {
    items.push({ name: 'Request White-Label', href: '/white-label-request', icon: InboxIcon });
  }

  const adminItems = audience.isAdmin
    ? [
        { name: 'Manage Users', href: '/admin/users', icon: UsersIcon },
        { name: 'Routing Policies', href: '/admin/routing-policies', icon: ScaleIcon },
        { name: 'Workspace Memory', href: '/admin/workspace-memory', icon: CircleStackIcon },
        { name: 'Tenants', href: '/admin/tenants', icon: BuildingOfficeIcon },
        { name: 'Models', href: '/admin/models', icon: CpuChipIcon },
        { name: 'Plans (people)', href: '/admin/plans', icon: CreditCardIcon },
        { name: 'Releases', href: '/admin/releases', icon: RocketLaunchIcon },
        { name: 'Notifications', href: '/admin/notifications', icon: BellAlertIcon },
      ]
    : [];

  /* Host-level visibility is applied after the build rather than inside it, so
   * that a role that earns an item and a host that forbids it stay two separate
   * readable rules. */
  const visible = items.filter((item) => {
    if (item.href === '/cloud-manager') return audience.showCloudManager;
    if (item.href.startsWith('/white-label')) {
      return audience.showWhiteLabelTab || audience.showWhiteLabelRequest;
    }
    return true;
  });

  return { audience, items: visible, adminItems };
}

/**
 * The app's TOP BAR (Rudy 2026-11-03, design "Option B": one bar across the top holds every page,
 * the chat opens first under it). Same audience rules as the sidebar above, which the marketing
 * homepage still draws; routes are unchanged, only where they are reached from.
 *
 * Returns `menus` in bar order. A menu is a plain link (`href`) or a dropdown (`sections`, each
 * `{ heading?, items }`; several sections open as a mega menu). `account` is what the avatar
 * menu lists above Appearance and Sign out.
 */
export function buildTopBarNav({ user, hostname = '' } = {}) {
  const audience = resolvePlatformAudience(user, hostname);
  const item = (name, href, icon, description) => ({ name, href, icon, description });

  const menus = [
    { id: 'chat', label: 'Chat', href: '/ai-chat', icon: ChatBubbleLeftIcon },
    {
      id: 'build',
      label: 'Build',
      sections: [
        { heading: 'Build', items: [
          item('Agents', '/ai-agents', BoltIcon, 'Harness, model, packs, channels'),
          item('Workspaces', '/workspaces', CubeIcon, 'Files, tools, backups, sessions'),
          item('Workflows', '/workflows', RectangleStackIcon, 'Scheduled and triggered runs'),
        ] },
        { heading: 'Runs', items: [
          item('Sessions', '/ai-sessions', ChartBarSquareIcon, 'Live and ended sessions'),
          item('Invocations', '/invocations', BoltIcon, 'Every model and tool call'),
        ] },
      ],
    },
    {
      id: 'models',
      label: 'Models',
      sections: [{ items: [
        item('Models & keys', '/models', CpuChipIcon, 'Models, your keys, plans'),
        item('Policies', '/routing-rules', ScaleIcon, 'Routing rules'),
      ] }],
    },
  ];

  // The marketplace is a plain link unless the viewer also sells or reviews (grants, never roles).
  const marketItems = [item('Browse marketplace', '/marketplace', RectangleStackIcon, 'Packs, workflows, MCP servers')];
  if (audience.canPublishMarketplace) marketItems.push(item('Sell', '/marketplace/sell', BanknotesIcon, 'Your listings and offers'));
  if (audience.canReviewMarketplace) marketItems.push(item('Report Review', '/marketplace/review', ClipboardDocumentCheckIcon, 'Readiness reports to sign'));
  menus.push(marketItems.length === 1
    ? { id: 'marketplace', label: 'Marketplace', href: '/marketplace', icon: RectangleStackIcon }
    : { id: 'marketplace', label: 'Marketplace', sections: [{ items: marketItems }] });

  menus.push({ id: 'integrations', label: 'Integrations', href: '/integrations', icon: LinkIcon });

  const cloudItems = [item('Dashboard', '/dashboard', HomeIcon, 'Cloud accounts at a glance')];
  if (audience.showCloudManager && (audience.isDirectCustomer || audience.isPlatformUser)) {
    cloudItems.push(item(audience.isDirectCustomer ? 'Compute' : 'Cloud Manager', '/cloud-manager', CloudIcon, 'Accounts, workloads, budgets'));
  }
  if (audience.canManageVoiceInfra) {
    cloudItems.push(item('Voice Infrastructure', '/voice-infrastructure', PhoneIcon, 'Retell profiles, voice workspaces'));
  }
  menus.push({ id: 'cloud', label: 'Cloud', sections: [{ items: cloudItems }] });

  if (audience.isAdmin) {
    menus.push({
      id: 'admin',
      label: 'Admin',
      admin: true,
      sections: [
        { heading: 'People and tenants', items: [
          item('Manage Users', '/admin/users', UsersIcon, 'Every account on the platform'),
          item('Tenants', '/admin/tenants', BuildingOfficeIcon, 'Packages, features, promotions'),
          item('Plans (people)', '/admin/plans', CreditCardIcon, 'Plans people subscribe to'),
        ] },
        { heading: 'Models and routing', items: [
          item('Model catalog', '/admin/models', CpuChipIcon, 'Providers, tiers, tenant access'),
          item('Routing Policies', '/admin/routing-policies', ScaleIcon, 'Platform-wide routing'),
          item('Workspace Memory', '/admin/workspace-memory', CircleStackIcon, 'What memory learns, platform-wide'),
        ] },
        { heading: 'Operations', items: [
          item('Releases', '/admin/releases', RocketLaunchIcon, 'What runs where'),
          item('Notifications', '/admin/notifications', BellAlertIcon, 'Messages to users and tenants'),
        ] },
      ],
    });
  }

  const account = [
    item('Account', '/account', UsersIcon, 'Name, email, password'),
    item('Credits', '/credits', CreditCardIcon, 'Balance, usage, history'),
  ];
  // White-label is the viewer's own business, so it sits with the account (BlueBear's admins use Admin › Tenants).
  if (audience.showWhiteLabelTab && !audience.isAdmin) {
    account.push(item('White-Label', '/white-label', SparklesIcon, 'Your white-label tenant'));
  } else if (audience.showWhiteLabelRequest) {
    account.push(item('Request White-Label', '/white-label-request', InboxIcon, 'Run BlueBear under your brand'));
  }

  return { audience, menus, account };
}

/** The bar menu a pathname belongs to ('account' for the avatar menu's pages), or null. */
/*
 * The ChatGPT-style navigation (Rudy 2026-11-03, Phil's ask; design "Option A", kept beside the top bar and switched
 * per person in the account menu): every page except the chat sits in one Settings view, its sections listed on the
 * left. The sections come from the same model as the top bar, so the two navigations always reach the same pages.
 */
export function buildSettingsSections(nav) {
  const sections = [{ id: 'account', label: 'Account', items: nav.account }];
  const links = [];
  for (const menu of nav.menus) {
    if (menu.id === 'chat') continue;
    if (menu.href) {
      links.push({ name: menu.label, href: menu.href, icon: menu.icon });
      continue;
    }
    sections.push({ id: menu.id, label: menu.label, admin: Boolean(menu.admin), items: menu.sections.flatMap((s) => s.items) });
  }
  // Single-link menus (Marketplace, Integrations) share one section, after Build and Models.
  if (links.length) sections.splice(Math.min(3, sections.length), 0, { id: 'connect', label: 'Connect', items: links });
  return sections;
}

/** The first admin page (the sidebar's shield button), or null for a viewer without the Admin menu. */
export function firstAdminHref(nav) {
  const admin = nav.menus.find((m) => m.admin);
  return admin?.sections?.[0]?.items?.[0]?.href ?? null;
}

/** The handful of pages the sidebar's account menu links straight to (the rest are in Settings). */
export function quickLinks(nav) {
  const wanted = ['/workspaces', '/ai-agents', '/models', '/marketplace'];
  const all = nav.menus.flatMap((m) => (m.href ? [{ name: m.label, href: m.href, icon: m.icon }] : m.sections.flatMap((s) => s.items)));
  return wanted.map((href) => all.find((i) => i.href === href)).filter(Boolean);
}

export function activeTopBarMenu(nav, pathname) {
  for (const menu of nav.menus) {
    if (menu.href && isNavItemActive(pathname, menu.href)) return menu.id;
    if (menu.sections?.some((s) => s.items.some((i) => isNavItemActive(pathname, i.href)))) return menu.id;
  }
  return nav.account.some((i) => isNavItemActive(pathname, i.href)) ? 'account' : null;
}

/** True when `pathname` is the item's own route or something beneath it. */
export function isNavItemActive(pathname, href) {
  return pathname === href || String(pathname || '').startsWith(`${href}/`);
}

export default buildPlatformNav;
